import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CafDashboardWindow, CafDecisionRow, listCafDecisions } from '../../../api/cafCorpus';
import { HOLD_GROUPS, HoldGroupKey } from '../copy';
import { formatDayTime } from '../format';
import { Icon } from '../Icon';
import { ActionPill } from '../LabelForm';
import { senderEmail } from '../lenders';
import { DecisionFilter, matchesFilter, matchesReason, reasonLine, staffCheckView } from './decisionStory';

const PAGE = 100;

/** Our own abort, not a real failure -- never shown as "couldn't load". */
const cancelled = (error: unknown, signal: AbortSignal): boolean => {
  const value = error as { code?: string; name?: string };
  return signal.aborted || value?.code === 'ERR_CANCELED' || value?.name === 'CanceledError' || value?.name === 'AbortError';
};

interface Props {
  days: CafDashboardWindow;
  lenderOf: (sender: string) => string | null;
  reason: HoldGroupKey | null;
  onClearReason: () => void;
  onOpen: (row: CafDecisionRow, flags: { enriched: boolean; reasons: boolean }) => void;
  /** Bump to reload after feedback or a fix. */
  refreshKey: number;
  /** Every page of rows as it loads, so an open drawer can swap in its reloaded row. */
  onLoaded?: (rows: CafDecisionRow[]) => void;
}

export const RecentDecisions: React.FC<Props> = ({ days, lenderOf, reason, onClearReason, onOpen, refreshKey, onLoaded }) => {
  const [filter, setFilter] = useState<DecisionFilter>('all');
  const [rows, setRows] = useState<CafDecisionRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [flags, setFlags] = useState({ enriched: false, reasons: false });
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState('');
  const generation = useRef(0);
  const controllerRef = useRef<AbortController | null>(null);
  const loadedRef = useRef(onLoaded);
  loadedRef.current = onLoaded;
  // A reason bar only lists marked-as-manual decisions, so it asks the server for
  // them exactly as the "Marked as manual" chip does (final wave item 11).
  const serverStaffOnly = filter === 'staff' || reason !== null;

  const load = useCallback(async (after: string | null) => {
    generation.current += 1;
    const mine = generation.current;
    // A load that's already in flight (an effect re-run, or a manual retry/show-more click
    // while one is pending) is superseded -- stop it rather than let it race this one.
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setLoading(true);
    setProblem('');
    try {
      const page = await listCafDecisions({
        days, limit: PAGE, enrich: true, ...(serverStaffOnly ? { action: 'MANUAL_REVIEW' as const } : {}), ...(after ? { cursor: after } : {}),
      }, controller.signal);
      if (mine !== generation.current) return;
      setRows((prev) => (after ? [...prev, ...page.items] : page.items));
      setCursor(page.next_cursor);
      setFlags({ enriched: page.enriched === true, reasons: page.enriched === true && page.reasons === true });
      loadedRef.current?.(page.items);
    } catch (err) {
      if (!cancelled(err, controller.signal) && mine === generation.current) setProblem("Couldn't load recent decisions.");
    } finally {
      if (!controller.signal.aborted && mine === generation.current) setLoading(false);
    }
  }, [days, serverStaffOnly]);

  useEffect(() => {
    void load(null);
    return () => { generation.current += 1; controllerRef.current?.abort(); };
  }, [load, refreshKey]);

  useEffect(() => {
    if (!flags.enriched && (filter === 'wrong' || filter === 'unchecked')) setFilter('all');
  }, [flags.enriched, filter]);

  const visible = rows
    .filter((row) => matchesFilter(row, filter, flags.enriched))
    .filter((row) => !reason || matchesReason(row, reason, flags.reasons));
  const reasonLabel = reason ? HOLD_GROUPS.find((group) => group.key === reason)?.label : null;
  const chip = (key: DecisionFilter, label: string) => (
    <button key={key} type="button" className="cur-chip" aria-pressed={filter === key} onClick={() => setFilter(key)}>{label}</button>
  );

  return (
    <div>
      <div className="cur-chips">
        {chip('all', 'All')}
        {/* Ryan's ruling: "Marked as manual", never "Sent to staff" -- the chip key and the
            server-side filter both stay 'staff' / action=MANUAL_REVIEW; only the label changed. */}
        {chip('staff', 'Marked as manual')}
        {flags.enriched && chip('wrong', 'Marked wrong')}
        {flags.enriched && chip('unchecked', 'Not checked yet')}
        {reasonLabel && (
          <span className="cur-chip cur-chip--reason">
            Reason: {reasonLabel}
            <button type="button" className="cur-linkish" aria-label="Clear reason filter" onClick={onClearReason}>
              <Icon name="x" size="xs" />
            </button>
          </span>
        )}
      </div>
      {problem && (
        <div className="cur-alert cur-alert--error" role="alert">
          <span className="cur-grow">{problem}</span>
          <button type="button" className="ui-btn ui-btn--danger ui-btn--sm" onClick={() => { void load(null); }}>Try again</button>
        </div>
      )}
      {visible.length > 0 ? (
        <div className="cur-table-wrap">
          <table className="cur-tbl" aria-label="Recent decisions">
            <colgroup>
              <col style={{ width: 'calc(18ch + 24px)' }} /><col style={{ width: '17%' }} /><col /><col style={{ width: '27%' }} /><col style={{ width: 132 }} />
            </colgroup>
            <thead><tr><th>Handled</th><th>Lender</th><th>Email</th><th>What the AI did</th><th>Staff check</th></tr></thead>
            <tbody>
              {visible.map((row) => {
                const check = staffCheckView(row, flags.enriched);
                const why = reasonLine(row, flags.reasons);
                return (
                  <tr key={row.id} className="cur-row-click" tabIndex={0} onClick={() => onOpen(row, flags)}
                    onKeyDown={(event) => {
                      // Only when the row itself has focus -- a key bubbling up from a
                      // future interactive child (a link, a button) must not also open it.
                      if (event.target !== event.currentTarget) return;
                      if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onOpen(row, flags); }
                    }}>
                    <td className="cur-nowrap cur-tnum cur-muted">{formatDayTime(row.processed_at)}</td>
                    <td><div className="cur-who"><strong>{lenderOf(row.email_from || '') ?? senderEmail(row.email_from)}</strong></div></td>
                    <td><span className="cur-ellip" title={row.subject || ''}>{row.subject || '(No subject)'}</span></td>
                    <td><ActionPill action={row.action} past />{why && <span className="cur-reason-line">{why}</span>}</td>
                    <td><span className={`cur-verdict ${check.className}`}>{check.icon && <Icon name={check.icon} size="sm" />}{check.label}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        !loading && (
          <div className="cur-empty">
            {/* More pages may still hold a match -- "no decisions" would be an overclaim
                while there's more the staff hasn't seen yet. */}
            <strong>{cursor ? 'No matches on the pages loaded so far.' : 'No decisions match.'}</strong>
            {(filter !== 'all' || reason) && (
              <button type="button" className="cur-linkish" onClick={() => { setFilter('all'); onClearReason(); }}>Clear filters</button>
            )}
          </div>
        )
      )}
      {loading && <p className="cur-hint">Loading…</p>}
      {cursor && !loading && (
        <div className="cur-more-row">
          <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" onClick={() => { void load(cursor); }}>Show more</button>
        </div>
      )}
    </div>
  );
};
